import {ResultFieldSettingContext} from "./contexts/resultFieldSettingProvider";
import {operationBranch} from "./lib/orderLinks.mjs";
import {UnsavedChangesGuard} from "./components/unsavedChanges/unsavedChanges";
import React, { Suspense, useContext, useEffect, useLayoutEffect, useState } from "react";
// react-router v6
import { Routes, Route, Navigate, useParams, useLocation, unstable_HistoryRouter as HistoryRouter } from "react-router-dom";
import {onDemoReadError} from "./firebase";
import { history } from "./history";
import { hasAdminAccess } from "./lib/accessPolicy.mjs";
//CSS
import "./App.css";
//compoenents
import AppLogout from "./components/autologout";
import Header from "./components/header/header";
import NotFound from "./components/notFound/notFound";
import logo from "./assets/img/logo/final2.png";

//current user context hook
import { AuthReadyContext, CurrentUserContext } from './contexts/currentUserProvider';

// context provider
import UsersProvider from "./contexts/usersProvider";
import { ContainerProvider } from "./contexts/containerProvider";
import { DeliveryOrderProvider } from "./contexts/deliveryOrderProvider";
import PlanItemETAProvider from "./contexts/planItem_eta_Provider";
import PlanItemLFDProvider from "./contexts/planItem_lfd_Provider";
import PlanItemToDoProvider from "./contexts/planItem_todo_Provider";
import PlanItemPICKUPProvider from "./contexts/planItem_pickup_Provider";
import PlanItemAPPProvider from "./contexts/planItem_app_Provider";
import PlanItemN2AProvider from "./contexts/planItem_n2a_Provider";
import PlanItemHISTORYProvider from "./contexts/planItem_history_Provider";
import PlanItemDELIVERYProvider from "./contexts/planItem_delivery_Provider";
import PlanItemRETURNProvider from "./contexts/planItem_return_Provider";

// Lazy-loaded routes (only downloaded when navigated to)
const Landing = React.lazy(() => import("./components/landing/landing"));
const Login = React.lazy(() => import("./components/login/login"));
const Account = React.lazy(() => import("./components/account/account"));
const AccountIndex = React.lazy(() => import("./components/accountIndex/accountIndex"));
const Dashboard = React.lazy(() => import("./components/dashboard/dashboard"));
const Report = React.lazy(() => import("./components/report/report"));
const Operation = React.lazy(() => import("./components/operation/operation"));
const Messaging = React.lazy(() => import("./components/messaging/messaging"));
const PlanningHeader = React.lazy(() => import("./components/planningheader/planningheader"));
const PlanItem = React.lazy(() => import("./components/planItem/planItem"));
const PlanItem2 = React.lazy(() => import("./components/planItem/planItem2"));
const PlanningExportAction = React.lazy(() => import("./components/planItem/planningExportAction"));
const RoutingCalender = React.lazy(() => import("./components/routingCalender/routingCalender"));

// Role/access predicates preserved from the previous @reach/router conditional-path auth.
function isActiveUser(user) {
  return user !== null && user !== undefined && user.status === "active";
}
function hasOperationAccess(user) {
  return isActiveUser(user)
    && (user.role === "admin" || user.role === "manager" || user.role.indexOf("member") !== -1);
}
function hasPlanningAccess(user) {
  return isActiveUser(user)
    && (user.role === "admin" || user.role === "manager" || user.role.indexOf("member1") !== -1);
}
function hasUserAccess(user) {
  return user !== null && user !== undefined;
}

// Redirect guard that replaces the old "conditional path" access pattern:
// when the user is not allowed, we redirect to the same fallback the old code
// remapped the route to (default "/dashboard", "/" for the account page).
function RequireAccess({ allowed, redirectTo = "/dashboard", children }) {
  return allowed ? children : <Navigate to={redirectTo} replace />;
}

function resetWindowScroll() {
  if (typeof window === "undefined") {
    return;
  }

  window.scrollTo({ top: 0, left: 0, behavior: "auto" });

  if (document.documentElement) {
    document.documentElement.scrollTop = 0;
  }

  if (document.body) {
    document.body.scrollTop = 0;
  }
}

function isProtectedPathname(pathname) {
  return pathname.startsWith("/operation")
    || pathname.startsWith("/messaging")
    || pathname.startsWith("/report")
    || pathname.startsWith("/admin")
    || pathname.startsWith("/user")
    || pathname.startsWith("/planning");
}

function DisplayAccountIndex() {
  return (
    <UsersProvider>
      <AccountIndex />
    </UsersProvider>
  );
}

function DisplayAccount() {
  return <Account />;
}

function DisplayOperation() {
  const { docRef } = useParams();
  const {search} = useLocation();
  const requestedBranch = operationBranch(search);
  const {resultFieldSetting, dispatchResultFieldSetting} = useContext(ResultFieldSettingContext);
  useEffect(() => {
    if (requestedBranch && requestedBranch !== resultFieldSetting.view) dispatchResultFieldSetting({type: "UPDATE_RESULTFIELD", resultField: "view", resultFieldInfo: requestedBranch});
  }, [requestedBranch, resultFieldSetting.view, dispatchResultFieldSetting]);
  // Do not mount/read Operations against the old branch while changing context.
  if (requestedBranch && requestedBranch !== resultFieldSetting.view) return <div role="status">Opening DO in {requestedBranch === "NY" ? "New York" : "Chicago"}…</div>;
  return (
    <DeliveryOrderProvider>
      <ContainerProvider>
        <Operation docRef={docRef} />
      </ContainerProvider>
    </DeliveryOrderProvider>
  );
}

function DisplayReport(props) {
  return (
    <DeliveryOrderProvider>
      <ContainerProvider>
        <Report />
      </ContainerProvider>
    </DeliveryOrderProvider>
  );
}

function DisplayPlanningRoute(props) {
  const { provider: Provider, itemComponent: ItemComponent, routingContainerState, branchCode = "NY" } = props;

  return (
    <div>
      <DeliveryOrderProvider>
        <ContainerProvider>
          <Provider branchCode={branchCode}>
            <PlanningHeader
              branchCode={branchCode}
              action={<PlanningExportAction routingContainerState={routingContainerState} branchCode={branchCode} />}
            />
            <ItemComponent routingContainerState={routingContainerState} branchCode={branchCode} />
          </Provider>
        </ContainerProvider>
      </DeliveryOrderProvider>
    </div>
  );
}

function DisplayRoutingRoute(props) {
  const { branchCode = "NY", isHistory, routingContainerState } = props;
  const [focusDate, setFocusDate] = React.useState(new Date());
  const Provider = isHistory ? PlanItemHISTORYProvider : PlanItemAPPProvider;

  return (
    <div>
      <PlanningHeader branchCode={branchCode} />
      <DeliveryOrderProvider>
        <ContainerProvider>
          <Provider branchCode={branchCode} focusDate={focusDate}>
            <RoutingCalender
              routingContainerState={routingContainerState}
              branchCode={branchCode}
              isHistory={isHistory === true}
              onNavigate={isHistory ? setFocusDate : undefined}
            />
          </Provider>
        </ContainerProvider>
      </DeliveryOrderProvider>
    </div>
  );
}

const planningRouteDefinitions = [
  { key: "eta", routeSegment: "eta", provider: PlanItemETAProvider, itemComponent: PlanItem, routingContainerState: "eta" },
  { key: "lfd", routeSegment: "lfd", provider: PlanItemLFDProvider, itemComponent: PlanItem, routingContainerState: "lfd" },
  { key: "pickup", routeSegment: "pickup", provider: PlanItemPICKUPProvider, itemComponent: PlanItem, routingContainerState: "pickup" },
  { key: "delivery", routeSegment: "delivery", provider: PlanItemDELIVERYProvider, itemComponent: PlanItem2, routingContainerState: "delivery" },
  { key: "todo", routeSegment: "todo", provider: PlanItemToDoProvider, itemComponent: PlanItem2, routingContainerState: "todo" },
  { key: "n2a", routeSegment: "n2a", provider: PlanItemN2AProvider, itemComponent: PlanItem2, routingContainerState: "n2a" },
  { key: "routing", routeSegment: "routing", isRouting: true, routingContainerState: undefined },
  { key: "routingHistory", routeSegment: "routingHistory", isRouting: true, isHistory: true, routingContainerState: undefined },
  { key: "return", routeSegment: "return", provider: PlanItemRETURNProvider, itemComponent: PlanItem, routingContainerState: "return" },
];

function App(props) {
  const [demoReadError, setDemoReadError] = useState(false);
  useEffect(() => onDemoReadError(() => setDemoReadError(true)), []);
  const currentUser = useContext(CurrentUserContext);
  const authReady = useContext(AuthReadyContext);
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";

  useEffect(() => {
    if (typeof window !== "undefined" && window.history && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const handlePageShow = () => {
      resetWindowScroll();
      window.requestAnimationFrame(() => {
        resetWindowScroll();
      });
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    const unlisten = history.listen(() => {
      resetWindowScroll();
      window.requestAnimationFrame(() => {
        resetWindowScroll();
      });
    });

    return () => {
      unlisten();
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    resetWindowScroll();

    const rafId = window.requestAnimationFrame(() => {
      resetWindowScroll();
    });

    const timeoutId = window.setTimeout(() => {
      resetWindowScroll();
    }, 120);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [pathname, authReady]);

  if (!authReady && isProtectedPathname(pathname)) {
    return (
      <div className="app">
        <div className="appBootShell">
          <div className="appBootCard">
            <div className="appBootTruckLane">
              <img src={logo} alt="WSI logo" className="appBootTruck" />
              <div className="appBootMotionLine appBootMotionLineOne" />
              <div className="appBootMotionLine appBootMotionLineTwo" />
              <div className="appBootMotionLine appBootMotionLineThree" />
            </div>
            <div className="appBootText">Loading portal...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <HistoryRouter history={history} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppLogout enabled={Boolean(currentUser)}>
        <Header />
        <UnsavedChangesGuard/>
        {demoReadError && currentUser?.role === "admin" && currentUser?.demoMode === true &&
          <div role="alert" style={{padding: "10px 24px", background: "#fce4e4", color: "#8b0000"}}>
            Demo data could not load. Confirm demo indexes and rules are deployed, then refresh this page.
          </div>}
        <Suspense fallback={
          <div className="appBootShell">
            <div className="appBootCard">
              <div className="appBootTruckLane">
                <img src={logo} alt="WSI logo" className="appBootTruck" />
                <div className="appBootMotionLine appBootMotionLineOne" />
                <div className="appBootMotionLine appBootMotionLineTwo" />
                <div className="appBootMotionLine appBootMotionLineThree" />
              </div>
              <div className="appBootText">Loading portal...</div>
            </div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<Landing page="about" />} />
            <Route path="/service" element={<Landing page="service" />} />
            <Route path="/services" element={<Landing page="service" />} />
            <Route path="/location" element={<Landing page="location" />} />
            <Route path="/coverage" element={<Landing page="location" />} />
            <Route path="/contact" element={<Landing page="contact" />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin/*" element={
              <RequireAccess allowed={hasAdminAccess(currentUser)}>
                <DisplayAccountIndex />
              </RequireAccess>
            } />
            <Route path="/operation" element={
              <RequireAccess allowed={hasOperationAccess(currentUser)}>
                <DisplayOperation />
              </RequireAccess>
            } />
            <Route path="/messaging/*" element={
              <RequireAccess allowed={hasAdminAccess(currentUser)}>
                <Messaging />
              </RequireAccess>
            } />
            <Route path="/operation/:docRef" element={
              <RequireAccess allowed={hasOperationAccess(currentUser)}>
                <DisplayOperation />
              </RequireAccess>
            } />
            <Route path="/user" element={
              <RequireAccess allowed={hasUserAccess(currentUser)} redirectTo="/">
                <DisplayAccount />
              </RequireAccess>
            } />
            <Route path="/report" element={
              <RequireAccess allowed={hasOperationAccess(currentUser)}>
                <DisplayReport />
              </RequireAccess>
            } />
            {["NY", "CHI"].flatMap(branchCode =>
              planningRouteDefinitions.map(route => {
                const path = branchCode === "NY"
                  ? `/planning/${route.routeSegment}`
                  : `/planning_chi/${route.routeSegment}`;

                const element = route.isRouting ? (
                  <DisplayRoutingRoute
                    branchCode={branchCode}
                    isHistory={route.isHistory === true}
                    routingContainerState={route.routingContainerState}
                  />
                ) : (
                  <DisplayPlanningRoute
                    branchCode={branchCode}
                    provider={route.provider}
                    itemComponent={route.itemComponent}
                    routingContainerState={route.routingContainerState}
                  />
                );

                return (
                  <Route
                    key={`${route.key}-${branchCode}`}
                    path={path}
                    element={
                      <RequireAccess allowed={hasPlanningAccess(currentUser)}>
                        {element}
                      </RequireAccess>
                    }
                  />
                );
              })
            )}

            <Route path="/login" element={<Login />} />
            <Route path="/inactive" element={<Login />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AppLogout>
      </HistoryRouter>
    </div>
  );
}
export default App;
